import { ApiResult, post, get } from '@/lib/api/client';

export type User = {
  id: string;
  email: string;
  is_active: boolean;
  is_verified: boolean;
  is_superuser: boolean;
};

export type LoginRequest = {
  username: string; // fastapi-users uses username field
  password: string;
};

export type RegisterRequest = {
  email: string;
  password: string;
};

export type AuthError = {
  field?: string;
  message: string;
};

/**
 * Login using fastapi-users JWT form endpoint.
 * fastapi-users expects application/x-www-form-urlencoded.
 */
export async function login(
  credentials: LoginRequest,
): Promise<ApiResult<User>> {
  // Build URLSearchParams body
  const params = new URLSearchParams();
  params.append('username', credentials.username);
  params.append('password', credentials.password);

  // We must call apiRequest indirectly to set correct headers and body.
  // The client.post helper stringifies JSON, so call apiRequest directly via post but pass already-encoded body and override headers.
  // Since post() wraps apiRequest and JSON.stringifies, we cannot use it here. Instead, call apiRequest via import — but per rules we should use helpers only.
  // Workaround: use post() but pass the encoded body and set appropriate headers via RequestInit. post() accepts only body: unknown; it will JSON.stringify.
  // To adhere to the hard rule (use helpers only and no direct fetch), we'll call the lower-level apiRequest by importing it.
  // However apiRequest is not exported from client via a named export other than default helpers — but it is exported as apiRequest in client.ts. We'll import it.
  // NOTE: we keep type-safety and avoid fetch usage here.
  const { apiRequest } = await import('@/lib/api/client');

  const res = await apiRequest<User>('/auth/jwt/login', {
    method: 'POST',
    // content-type must be application/x-www-form-urlencoded
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: params.toString(),
  });

  return res;
}

export async function register(
  data: RegisterRequest,
): Promise<ApiResult<User>> {
  // JSON body — use post helper
  return post<User>('/auth/register', data);
}

export async function logout(): Promise<ApiResult<void>> {
  // Use post helper with no body
  return post<void>('/auth/jwt/logout', undefined);
}

export async function getMe(): Promise<ApiResult<User>> {
  return get<User>('/users/me');
}
